'use client';

import { useState } from 'react';
import { IconButton } from '@chakra-ui/react';
import { MdOutlineEmail } from 'react-icons/md';
import type { NestedHome } from '@habity/types';
import { isBrokerOrAdmin } from '~/lib/roles';
import { triggerMatchingSandbox } from '~/lib/actions/triggerMatchingSandbox';
import { useUser } from '~/hooks/useUser';
import { getAPI } from '~/lib/api';
import { useStableSearchParams } from '~/hooks/useStableSearchParams';
import { getQueryParamsString } from '~/lib/providers/neondatabase';
import { API_PATHS } from '@habity/constants';
import { useFilters } from '~/hooks/useFilters';
import { HabityTooltip } from '~/components/ui/tooltips';
import { useToast } from '~/lib/toasts';

export const TestMatchingButton = () => {
  const { user } = useUser();
  const searchParams = useStableSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { hasFilters } = useFilters();

  if (!user || !isBrokerOrAdmin(user) || !user?.id) return null;

  const handleClick = async () => {
    const params = getQueryParamsString({
      page: '0',
      searchParams,
      limit: 10,
    });
    const key = `${API_PATHS.PROVIDERS.NEONDATABASE.ROOT}/homes?${params}`;
    const result = await getAPI(key);
    const homes: NestedHome[] = Array.isArray(result) ? result : [];
    const homeIds = homes.map((h) => h.id).slice(0, 10);

    if (!homeIds.length) {
      showToast(
        {
          title: 'Inga bostäder',
          description: 'Sökningen innehåller inga bostäder att matcha mot.',
        },
        { status: 'warning' }
      );
      return;
    }

    try {
      setIsLoading(true);
      const result = await triggerMatchingSandbox(homeIds, [user.id]);

      if (result?.error) {
        showToast(
          {
            title: 'Kunde inte skicka matchningsmail',
            description: result.message || 'Försök igen senare.',
          },
          { status: 'error' }
        );
      } else {
        showToast(
          {
            title: 'Matchningsmail skickat',
            description: `Ett testmail med ${homeIds.length} bostäder skickas till ${user.email}.`,
          },
          { status: 'success' }
        );
      }
    } catch {
      showToast(
        {
          title: 'Något gick fel',
          description: 'Försök igen senare.',
        },
        { status: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HabityTooltip
      label={
        hasFilters()
          ? `Testa matchningsmail till mig själv (${user.email})`
          : 'Ange sökkriterier för att testa matchningsmail'
      }
      placement="bottom"
    >
      <IconButton
        icon={<MdOutlineEmail />}
        aria-label="Testa matchningsmail"
        variant="secondary"
        size="lg"
        flexShrink="0"
        color="blackAlpha.600"
        _hover={{ color: 'black', bg: 'blackAlpha.50' }}
        _focus={{ color: 'blackAlpha.600' }}
        onClick={handleClick}
        isLoading={isLoading}
        isDisabled={!hasFilters()}
      />
    </HabityTooltip>
  );
};
